<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ResourceList from './ResourceList.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key => t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.WEBSITES.${key}`);

const websites = ref([]);
const showForm = ref(false);
const url = ref('');
const pendingDelete = ref(null);
const showDeleteModal = ref(false);

const fetchWebsites = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.listWebsites(props.hookId);
    websites.value = data;
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

onMounted(fetchWebsites);

const resetForm = () => {
  url.value = '';
  showForm.value = false;
};

const createWebsite = async () => {
  try {
    await MetaBusinessAgentAPI.createWebsite(props.hookId, url.value);
    useAlert(T('API.CREATE_SUCCESS'));
    resetForm();
    fetchWebsites();
  } catch (error) {
    useAlert(T('API.CREATE_ERROR'));
  }
};

const confirmDelete = item => {
  pendingDelete.value = item;
  showDeleteModal.value = true;
};

const deleteWebsite = async () => {
  try {
    await MetaBusinessAgentAPI.deleteWebsite(
      props.hookId,
      pendingDelete.value.id
    );
    useAlert(T('API.DELETE_SUCCESS'));
    fetchWebsites();
  } catch (error) {
    useAlert(T('API.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <Button :label="T('ADD_BUTTON')" @click="showForm = true" />
    </div>
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
    >
      <Input v-model="url" :label="T('URL_LABEL')" placeholder="https://" />
      <div class="flex justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click="resetForm"
        />
        <Button
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          @click="createWebsite"
        />
      </div>
    </div>
    <ResourceList
      :items="websites"
      :empty-state="T('EMPTY_STATE')"
      @delete="confirmDelete"
    >
      <template #default="{ item }">
        <p class="font-medium text-n-slate-12 m-0">{{ item.url }}</p>
        <p class="text-sm text-n-slate-11 m-0">
          {{ T('STATUS_LABEL') }}: {{ item.crawl_status || '—' }}
        </p>
      </template>
    </ResourceList>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteWebsite"
      :title="T('DELETE_CONFIRM_TITLE')"
      :message="T('DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
